'use client'

import { Dialog, DialogContent, DialogTrigger, DialogDescription, DialogTitle } from "./dialog"
import { Button } from "../buttons";
import type { ButtonProps } from "../../types/ButtonProps";
import { COMPANY_EMAIL, COMPANY_PHONE, CONTACT_METHODS } from "@shared/constants"
import { ReactNode } from "react"
import { MailOutlined, WhatsAppOutlined } from "@ant-design/icons"
import React from "react"
import { useWhatsAppUrl } from "../../hooks/useWhatsAppUrl"

interface ContactDialogProps {
  trigger?: ReactNode
  buttonProps?: ButtonProps
  buttonText?: string
  variant?: "default" | "ghost"
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

interface ContactMethodProps {
  icon: ReactNode
  href: string
  label: string
}

const CONTACT_ICONS: Record<string, React.ReactNode> = {
  [COMPANY_EMAIL]: <MailOutlined className="w-8 h-8" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />,
  [COMPANY_PHONE]: <WhatsAppOutlined className="w-8 h-8" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} />
}

const ContactMethod = ({ icon, href, label }: ContactMethodProps) => {
  const whatsAppUrl = useWhatsAppUrl()
  const finalHref = label === COMPANY_PHONE ? whatsAppUrl : href
  return (
    <div className="flex flex-col items-center justify-center p-6 text-center">
      <div className="text-4xl text-brand mb-4">
        {icon}
      </div>
      <a 
        href={finalHref}
        className="text-brand-dark hover:text-brand-secondary transition-colors text-sm md:text-base"
        target="_blank"
        rel="noopener noreferrer"
      >
        {label}
      </a>
    </div>
  )
}

export function ContactDialog({ 
  trigger, 
  buttonProps,
  buttonText = "Contact Us",
  variant = "default",
  isOpen,
  onOpenChange
}: ContactDialogProps) {
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild onClick={handleButtonClick}>
        {trigger || (
          <Button
            variant={variant}
            {...buttonProps}
          >
            {buttonText}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogTitle className="sr-only">
          Contact Us
        </DialogTitle>
        <div className="text-center mb-4">
          <h2 className="text-lg font-semibold">Get in Touch</h2>
          <p className="text-sm text-muted-foreground mt-1">Choose your preferred contact method</p>
        </div>
        <DialogDescription className="sr-only">
          Choose a method to contact us
        </DialogDescription>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 py-4">
          {CONTACT_METHODS.map((method, index) => (
            <React.Fragment key={method.label}>
              <ContactMethod
                icon={CONTACT_ICONS[method.label]}
                href={method.href}
                label={method.label}
              />
              {index < CONTACT_METHODS.length - 1 && (
                <div className="hidden md:block w-px h-16 bg-border" />
              )}
            </React.Fragment>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
